import type { Address } from '../../api/agwa-api-model';

/**
 * The pure address-state transitions for the site address editor.
 *
 * These are the decisions the editor's rules turn on - who owns the pin, when Google's wording is
 * adopted and how much of it, whether a place name survives - lifted out of the Lit component so
 * they can be exercised without a browser or the Google Maps SDK. The component keeps the side
 * effects (marker, map pan, reverse-geocode, finder); it delegates every state decision here.
 *
 * Keyed to documentation/engineering/site-address-editor-rules.md (R2, R4, R9, R10).
 */

/**
 * The wording fields of an address: everything Google returns as words, and the only fields an
 * adoption is allowed to copy.
 *
 * Deliberately not `Partial<Address>`. That admitted the pin (`latitude` / `longitude` /
 * `pinSource`), the provenance flags and the derived `components` list, none of which are wording
 * and none of which may be carried across by a blanks-fill - rule R2 turns on the pin surviving
 * wording adoption untouched. It also let `blanksFrom` claim every value was a string, which was
 * only true by accident of what the caller happened to pass.
 */
export type AddressTextParts = Partial<
  Pick<
    Address,
    | 'streetNumber'
    | 'streetName'
    | 'unitNumber'
    | 'suburb'
    | 'state'
    | 'stateFull'
    | 'postcode'
    | 'country'
    | 'countryCode'
    | 'formattedAddress'
    | 'placeName'
    | 'googlePlaceId'
  >
>;

/** Google's answer for a location, offered for the user (or the editor) to adopt. */
export interface AddressProposal {
  /** Google's assembled formatted address for the pin. */
  formattedAddress: string;
  /** The place id, when the resolution carried one. */
  placeId?: string;
  /** The structured parts Google returned. */
  parts: AddressTextParts;
  /**
   * Whether adopting this should discard any existing place name. True when the name no longer
   * applies (a moved pin, or a name that had to be ignored to get a match); false when the wording
   * was merely tidied and a legitimate name should stand (rule R4).
   */
  dropsPlaceName: boolean;
}

// A non-empty string, matching the component's own "already has a value" test exactly (a raw
// truthiness check, no trim) so this extraction preserves behaviour for every input.
const isFilled = (value: unknown): boolean =>
  typeof value === 'string' && value.length > 0;

/**
 * The subset of a proposal's parts the current address does not already have. Used so an automatic
 * adoption can fill gaps without overwriting anything the user holds (rule R9).
 */
export function blanksFrom(
  current: Partial<Address> | null | undefined,
  parts: AddressTextParts
): AddressTextParts {
  const blanks: AddressTextParts = {};
  // Every field of AddressTextParts is a string, so unlike the old keyof Address cast this one is
  // true rather than merely unchallenged - which is what lets the assignment below type-check.
  for (const [key, value] of Object.entries(parts) as [
    keyof AddressTextParts,
    string | undefined,
  ][]) {
    if (value && !isFilled(current?.[key])) {
      blanks[key] = value;
    }
  }
  return blanks;
}

/**
 * The patch that adopts Google's wording for a resolved location.
 *
 * - `source` becomes `GoogleGeocoded`: adopting the wording is the only thing that makes an address
 *   Google's; a hand-placed pin against typed words stays `Manual` until this runs (rule R2).
 * - `fillBlanksOnly` is the difference between helping and deciding: when the user explicitly
 *   adopts, the wording applies wholesale; when the editor adopts on its own it may only fill gaps,
 *   never overwrite (rule R9). It also never drops the place name in that mode (rule R4).
 * - The pin (`latitude`/`longitude`/`pinSource`) is deliberately untouched - adopting wording does
 *   not move or re-attribute the point.
 */
export function proposalPatch(
  current: Partial<Address> | null | undefined,
  proposal: AddressProposal,
  options: { fillBlanksOnly: boolean }
): Partial<Address> {
  const parts = options.fillBlanksOnly
    ? blanksFrom(current, proposal.parts)
    : proposal.parts;

  const keepExistingFormatted =
    options.fillBlanksOnly && isFilled(current?.formattedAddress);

  return {
    ...parts,
    ...(keepExistingFormatted
      ? {}
      : { formattedAddress: proposal.formattedAddress }),
    googlePlaceId: proposal.placeId,
    source: 'GoogleGeocoded',
    ...(proposal.dropsPlaceName && !options.fillBlanksOnly
      ? { placeName: undefined }
      : {}),
  };
}

/**
 * Whether the editor should adopt Google's wording without being asked, and how much of it (R10).
 *
 * A `ROOFTOP` match is a specific building, so its wording beats a hand-typed guess and is taken
 * whole - including when the field counts are equal (an address typed in full but abbreviated and
 * lowercase). Anything less may be the wrong place, so it only fills blanks, and only when there
 * are blanks to fill (otherwise the offer stays a manual choice).
 */
export function autoAdoptDecision(
  current: Partial<Address> | null | undefined,
  proposal: AddressProposal,
  exact: boolean
): { adopt: boolean; fillBlanksOnly: boolean } {
  if (exact) {
    return { adopt: true, fillBlanksOnly: false };
  }
  if (Object.keys(blanksFrom(current, proposal.parts)).length === 0) {
    return { adopt: false, fillBlanksOnly: true };
  }
  return { adopt: true, fillBlanksOnly: true };
}

/**
 * The patch for a coordinate set. `pinSource` records who placed the point, independent of `source`
 * (the wording): a person clicking or dragging owns the location even on a Google-worded address
 * (rule R2). The wording, place name and place id are untouched here.
 */
export function coordsPatch(
  lat: number,
  lng: number,
  origin: 'google' | 'manual'
): Partial<Address> {
  return {
    latitude: lat,
    longitude: lng,
    pinSource: origin === 'manual' ? 'Manual' : 'Google',
  };
}
